<template>
  <div class="store-builder-outer-container">
    <div class="center-middle overflow-container">
      <div class="middle-container">
        <Card :disableTitle="true">
          <h1>StoreBuilder</h1>
          <form>
            <FormSelect
              v-model="formValues.industry"
              name="industry"
              label="What industry are you in?"
              :options="industries"
              noneLabel="- Select an option -"
            />
            <FormSelect
              v-model="formValues.alreadySelling"
              name="already_selling_products"
              label="Are you already selling products?"
              :options="alreadySellingOptions"
              noneLabel="- Select an option -"
            />
            <FormSelect
              v-if="
                formValues.alreadySelling &&
                formValues.alreadySelling === 'selling_online_different_platform'
              "
              v-model="formValues.currentPlatform"
              name="platform_currently_using"
              label="Which platform?"
              :options="platformOptions"
              noneLabel="- Select an option -"
            />
            <FormSelect
              v-if="formValues.alreadySelling"
              v-model="formValues.howSell"
              name="plan_to_sell"
              label="How do you plan to sell?"
              :options="planSellOptions"
              noneLabel="- Select an option -"
            />
            <hr />
            <div
              v-if="
                formValues.howSell &&
                ['only_offline', 'online_and_offline'].includes(formValues.howSell)
              "
            >
              <label>When you sell in person, will you need any of the following?</label>
              <FormCheckbox
                v-model="formValues.salesMobileOrder"
                name="in_person_sales_needs[mobile_ordering]"
                label="A way to create orders (mobile ordering)"
              />
              <FormCheckbox
                v-model="formValues.salesMobileFunds"
                name="in_person_sales_needs[mobile_point_of_sale]"
                label="A way to collect funds (mobile point of sale)"
              />
              <FormCheckbox
                v-model="formValues.salesMobileCatalog"
                name="in_person_sales_needs[mobile_catalog]"
                label="A way to present your products (mobile catalog)"
              />
              <hr />
            </div>
            <FormSelect
              v-model="formValues.catalogSize"
              name="product_catalog_size"
              label="How many products are in your product catalog?"
              :options="catalogOptions"
              noneLabel="- Select an option -"
            />
            <hr />
            <label>Do your products have variations?</label>
            <FormCheckbox
              v-model="formValues.hasVariations"
              name="has_variations[has_variations]"
              label="Yes"
            />
            <hr />
            <label>
              What kind of content do you already have developed? (select all that apply)
            </label>
            <FormCheckbox
              v-model="formValues.contentExplainer"
              name="existing_content_types[explainer_video]"
              label="Explainer Video"
            />
            <FormCheckbox
              v-model="formValues.contentKeyDifferentiate"
              name="existing_content_types[key_differentiating_features]"
              label="Key Differentiating Features"
            />
            <FormCheckbox
              v-model="formValues.contentArticles"
              name="existing_content_types[articles_on_other_sites_telling_your_story]"
              label="Articles on other sites telling your story"
            />
            <FormCheckbox
              v-model="formValues.contentCaseStudies"
              name="existing_content_types[case_studies_of_happy_customers]"
              label="Case studies of happy customers"
            />
            <FormCheckbox
              v-model="formValues.contentReturnPolicy"
              name="existing_content_types[amazing_return_policy]"
              label="An amazing return policy"
            />
            <FormCheckbox
              v-model="formValues.contentSocial"
              name="existing_content_types[social_proof]"
              label="Social Proof (Instagram, Facebook, Twitter)"
            />
            <FormCheckbox
              v-model="formValues.contentOffers"
              name="existing_content_types[special_offers]"
              label="Special Offers"
            />
            <FormCheckbox
              v-model="formValues.contentCoupons"
              name="existing_content_types[special_coupons]"
              label="Special Coupons"
            />
            <FormCheckbox
              v-model="formValues.contentNewsletter"
              name="existing_content_types[email_newsletter_signups]"
              label="Email newsletter signups"
            />
            <FormCheckbox
              v-model="formValues.contentReviews"
              name="existing_content_types[customer_reviews]"
              label="Customer reviews"
            />
            <hr />
            <label>Load sample products?</label>
            <FormCheckbox
              v-model="formValues.loadProducts"
              name="demo_products[demo_products]"
              label="Yes, please import sample products into my store."
            />
            <Btn class="mt-4" variation="primary">Create my store!</Btn>
          </form>
        </Card>
      </div>
    </div>
  </div>
</template>

<script>
import Btn from "@/components/display/Btn";
import Card from "@/components/display/Card";
import FormCheckbox from "@/components/display/form/FormCheckbox";
import FormSelect from "@/components/display/form/FormSelect2";
import { reactive } from "@vue/composition-api";

export default {
  name: "StoreBuilder",
  components: {
    Btn,
    Card,
    FormCheckbox,
    FormSelect,
  },
  setup() {
    const formValues = reactive({
      industry: null,
      alreadySelling: null,
      howSell: null,
      currentPlatform: null,
      salesMobileOrder: false,
      salesMobileFunds: false,
      salesMobileCatalog: false,
      catalogSize: null,
      hasVariations: false,
      contentExplainer: false,
      contentKeyDifferentiate: false,
      contentArticles: false,
      contentCaseStudies: false,
      contentReturnPolicy: false,
      contentSocial: false,
      contentOffers: false,
      contentCoupons: false,
      contentNewsletter: false,
      contentReviews: false,
      loadProducts: false,
    });

    const industries = [
      {
        value: "apparel",
        label: "Apparel",
      },
      {
        value: "books",
        label: "Books / Music / Video",
      },
      {
        value: "electronics",
        label: "Electronics",
      },
      {
        value: "gifts",
        label: "Gifts",
      },
      {
        value: "health-beauty",
        label: "Health / Beauty",
      },
      {
        value: "home-goods",
        label: "Home Goods",
      },
      {
        value: "jewelry",
        label: "Jewelry",
      },
      {
        value: "petcare",
        label: "Petcare",
      },
      {
        value: "sports-hobbies",
        label: "Sports / Hobbies",
      },
      {
        value: "other",
        label: "Other",
      },
    ];

    const alreadySellingOptions = [
      {
        value: "selling_offline",
        label: "I'm selling offline right now",
      },
      {
        value: "selling_online_different_platform",
        label: "I'm selling online with a different platform",
      },
      {
        value: "not_selling_yet",
        label: "I'm not selling yet",
      },
    ];

    const planSellOptions = [
      {
        value: "only_online",
        label: "Only online",
      },
      {
        value: "only_offline",
        label: "Only offline",
      },
      {
        value: "online_and_offline",
        label: "Online and offline",
      },
    ];

    const platformOptions = [
      {
        value: "prestashop",
        label: "PrestaShop",
      },
      {
        value: "etsy",
        label: "Etsy",
      },
      {
        value: "squarespace",
        label: "Squarespace",
      },
      {
        value: "square",
        label: "Square",
      },
      {
        value: "ebay",
        label: "eBay",
      },
      {
        value: "amazon-store",
        label: "Amazon Store",
      },
      {
        value: "bigcommerce",
        label: "BigCommerce",
      },
      {
        value: "wix",
        label: "Wix",
      },
      {
        value: "vend",
        label: "Vend",
      },
      {
        value: "big-cartel",
        label: "Big Cartel",
      },
      {
        value: "magento",
        label: "Magento",
      },
      {
        value: "volusion",
        label: "Volusion",
      },
      {
        value: "woocommerce",
        label: "WooCommerce",
      },
      {
        value: "other",
        label: "Other",
      },
    ];

    const catalogOptions = [
      {
        value: "1",
        label: "1 product",
      },
      {
        value: "2 - 99",
        label: "2 - 99 products",
      },
      {
        value: "100+",
        label: "100+ products",
      },
    ];

    return {
      industries,
      alreadySellingOptions,
      planSellOptions,
      platformOptions,
      catalogOptions,
      formValues,
    };
  },
};
</script>

<style lang="scss" scoped>
.store-builder-outer-container {
  display: flex;
  flex-direction: column;
  height: 100%;

  .overflow-container {
    overflow: auto;
    padding: 1rem 0;
  }
  .middle-container {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    margin: auto;
    width: 100%;
    max-width: 500px;
    padding: 1rem;
  }
  .top-logo {
    max-width: 100%;
    width: 350px;
    margin-bottom: 1rem;
    text-align: center;
  }

  .loader-area {
    width: 100%;
    min-width: 300px;
    max-width: 350px;
    height: 350px;
    position: relative;
  }

  .login-container {
    min-width: 300px;
    padding: 1rem;

    .form-control {
      width: 100%;
    }
  }
}
</style>
